import { computed, Directive, inject, type OnInit, signal } from '@angular/core';
import { type AbstractControl, NgControl } from '@angular/forms';

import { controlStateSignal } from '@shared/utils/control-state';

/**
 * Styles a native `<input>` or `<textarea>` as a design-system control and reflects its reactive
 * validity into the DOM. It reads the host's own `NgControl` (from `formControlName`,
 * `formControl` or `ngModel`), so the error border and warning icon appear automatically once the
 * control is invalid and touched — no state input to wire up.
 *
 * Pair it with `<app-form-field>` for the label and the validation message; this directive owns
 * only the control box itself.
 */
@Directive({
  selector: 'input[appInput], textarea[appInput]',
  host: {
    class: 'input-control',
    '[class.input-control--error]': 'showError()',
    '[attr.aria-invalid]': 'showError() || null',
  },
})
export class InputDirective implements OnInit {
  private readonly ngControl = inject(NgControl, { optional: true, self: true });

  // `NgControl.control` is only populated after the reactive-forms directive initializes, so it is
  // captured in `ngOnInit` and exposed as a signal for the shared state helper to track.
  private readonly control = signal<AbstractControl | null>(null);
  private readonly state = controlStateSignal(this.control);

  protected readonly showError = computed(() => this.state().showError);

  ngOnInit(): void {
    this.control.set(this.ngControl?.control ?? null);
  }
}
